import { createFileRoute } from "@tanstack/react-router";
import { useCallback } from "react";
import { CircleSpinner } from "react-spinners-kit";

import LangHeading from "@/components/LangHeading";
import ModuleSelector from "@/components/ModuleSelector";
import PageNav from "@/components/PageNav";
import Counts from "@/features/editor/Counts";
import DocFormatting from "@/features/docs/DocFormatting";
import DocRow from "@/features/docs/DocRow";
import { useFilteredIds } from "@/hooks/useFilteredIds";
import { useTranslations } from "@/queries/useTranslations";
import { paginate } from "@/translationFiltering";
import { useResetPageOnSearchChange } from "@/hooks/useResetPageOnSearchChange";

export const Route = createFileRoute("/$version/docs/")({
    component: Docs,
});

function Docs() {
    const { version } = Route.useParams();
    const search = Route.useSearch();
    const navigate = Route.useNavigate();
    const { data: translations } = useTranslations(version);
    const { filtered, isComparePending } = useFilteredIds(version);

    const pageNr = search.page ?? 1;
    const resetPage = useCallback(() => {
        navigate({
            to: ".",
            search: (prev) => ({ ...prev, page: undefined }),
            replace: true,
        });
    }, [navigate]);

    useResetPageOnSearchChange({ page: pageNr, search, onReset: resetPage });

    if (!translations || !filtered) {
        return <CircleSpinner size={48} color="#666666" />;
    }

    const fullIds = filtered.docs;
    const { ids, pageCount } = paginate(fullIds, pageNr);

    return (
        <>
            <ModuleSelector contentType={"docs"} version={version} />
            <DocFormatting />
            <Counts currentCount={fullIds.length} totalCount={translations.docs.ids.length} />
            {isComparePending && <div>Loading compare version…</div>}
            <PageNav page={pageNr} maxPage={pageCount} version={version} contentType="docs" />
            <LangHeading />
            <div>
                {ids.map((id) => (
                    <DocRow key={id} id={id} version={version} />
                ))}
            </div>
            <PageNav page={pageNr} maxPage={pageCount} version={version} contentType="docs" />
        </>
    );
}
